import pLimit from "p-limit";
import { postFileUpload } from "@/api/files.api";

const uploadLimit = pLimit(3);
const CHUNK_SIZE = 10 * 1024 * 1024;

export async function startUpload({
    uploadFiles,
    curDir,
    overwrite,
    onStart,
    onProgress,
    onComplete,
    onFail,
    onFinish,
}) {
    const tasks = [];
    const summary = {
        success: 0,
        exists: 0,
        failed: 0,
    };

    for (const uploading of uploadFiles) {
        const task = uploadLimit(async () => {
            try {
                const result = await uploadFile({
                        uploading,
                        curDir,
                        overwrite,
                        onStart,
                        onProgress,
                    });
                if (result.type === "success") {
                    summary.success++;
                    onComplete?.(result);
                }
                else if (result.type === "exists") {
                    summary.exists++;
                    onComplete?.(result);
                }
            }
            catch (error) {
                summary.failed++;
                const fail = {
                    file: uploading.entry,
                    error,
                };
                onFail?.(fail);
            }
        });
        tasks.push(task);
    }
    await Promise.all(tasks);
    onFinish?.(summary);
}

export async function uploadChunk({
    file,
    curDir,
    targetPath,
    overwrite,
    onProgress,
}) {
    let start = 0;
    let chunkIndex = 0;
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    let lastResponse = null;
    while (start < file.size) {
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        const chunkFile =
            new File([chunk], file.name,
                {
                    type: file.type || "application/octet-stream"
                }
            );

        const formData = createUploadFormData({
                chunkFile,
                fileName: file.name,
                chunkIndex,
                totalChunks,
                curDir,
                targetPath,
                overwrite,
            });

        const response = await uploadChunkRequest({ formData, retry: 0,});
        // 마지막 응답 저장
        lastResponse = response;
        if (response.data.status === "exists file") {
            return {
                type: "exists",
                file,
                response,
                curDir,
                targetPath,
            };
        }
        onProgress?.({
            name: file.name,
            chunkSize: chunk.size,
            totalFileSize: file.size,
        });
        start = end;
        chunkIndex++;
    }
    return {
        type: "success",
        file,
        response: lastResponse,   // 추가
        curDir,
        targetPath,
    };
}

async function uploadFile({
    uploading,
    curDir,
    overwrite,
    onStart,
    onProgress,
}) {
    const file = await resolveEntryFile(uploading.entry);
    onStart?.({
        name: file.name,
        size: file.size,
        path: uploading.path,
    });
    const result = await uploadChunk({
            file,
            curDir,
            targetPath: uploading.path,
            overwrite,
            onProgress,
        });
    return {
        ...result,
        curDir,
        targetPath: uploading.path,
    };
}

function resolveEntryFile(entry) {
    if (entry instanceof File) {
        return Promise.resolve(entry);
    }
    return new Promise((resolve, reject) => {
            entry.file(
                resolve,
                reject
            );
        }
    );
}

async function uploadChunkRequest({ formData, retry, }) {
    try {
        const response = await postFileUpload(formData);
        return response;
    }
    catch (error) {
        if (retry < 3) {
            await delay(1000 * Math.pow(2, retry));
            return uploadChunkRequest({ formData, retry: retry + 1, });
        }
        throw error;
    }
}

function createUploadFormData({
    chunkFile,
    fileName,
    chunkIndex,
    totalChunks,
    curDir,
    targetPath,
    overwrite,
}) {
    const formData = new FormData();
    formData.append("file", chunkFile);
    formData.append("fileName", fileName);
    formData.append("chunkIndex", chunkIndex);
    formData.append("totalChunks", totalChunks);
    formData.append("curLocation", curDir);
    formData.append("targetLocation", targetPath);
    formData.append("checkBox", overwrite);

    return formData;
}

function delay(ms) {
    return new Promise(
        resolve =>
            setTimeout(
                resolve,
                ms
            )
    );
}


async function traverseEntry(entry, path, pendingFiles) {
    if (entry.isFile) {
        pendingFiles.push({
            entry,
            path: path.split("/").slice(0, -1).join("/"),
        });
        return;
    }

    const reader = entry.createReader();
    const entries = await readAllEntries(reader);

    for (const child of entries) {
        await traverseEntry(
            child,
            `${path}/${child.name}`,
            pendingFiles
        );
    }
}

async function readAllEntries(reader) {
    const result = [];
    while (true) {
        const entries = await new Promise(resolve => reader.readEntries(resolve));
        if (!entries.length)
            break;
        result.push(...entries);
    }
    return result;
}


export async function collectFiles(files) {
    const pendingFiles = [];
    for (const file of files) {
        const relativePath = file.webkitRelativePath || "";
        pendingFiles.push({
            entry: file,
            path: relativePath.split("/").slice(0, -1).join("/"),
        });
    }
    return pendingFiles;
}

export async function collectDropFiles(items) {
    const pendingFiles = [];
    for (const item of items) {
        const entry = item.webkitGetAsEntry();
        if (!entry)
            continue;

        await traverseEntry(entry, entry.name, pendingFiles);
    }
    return pendingFiles;
}